import classNames from 'classnames';
import type { CSSProperties, FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';

import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { MemberAvatarUnconnected } from '@trello/member-avatar';
import { convertToPIIString } from '@trello/privacy';
import { RouterLink } from '@trello/router/router-link';
import type { TemplatesTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { UnsplashTracker } from '@trello/unsplash';
import { makeTemplateUrlSlug } from '@trello/urls';

import { ViewsAndCopies } from 'app/src/components/BoardMenuViewsAndCopies';
import { useTemplateBackgroundStyle } from 'app/src/components/CreateBoardFromTemplate';
import { TemplatesTileSkeleton } from './TemplatesTileSkeleton';

import styles from './TemplatesTile.less';

function getBackgroundAfterStyle(hover: boolean) {
  const css: CSSProperties = {};
  css.opacity = hover ? 1 : 0;
  return css;
}

interface TemplatesTileProps {
  avatarShape: string;
  avatarUrl: string;
  category: string;
  backgroundColor?: string;
  backgroundUrl?: string;
  blurb: string;
  boardId: string;
  byline: string;
  idOrganization: string;
  shortLink: string;
  title: string;
  isFeatured?: boolean;
  viewCount: number;
  copyCount: number;
  analyticsSource?: SourceType;
}

export const TemplatesTile: FunctionComponent<TemplatesTileProps> = ({
  avatarShape,
  avatarUrl,
  backgroundColor,
  backgroundUrl,
  blurb,
  boardId,
  byline,
  category,
  shortLink,
  title,
  isFeatured,
  viewCount,
  copyCount,
  analyticsSource,
}) => {
  useEffect(() => {
    if (backgroundUrl) {
      UnsplashTracker.trackOncePerInterval(backgroundUrl);
    }
  }, [backgroundUrl]);

  const [hover, setHover] = useState(false);
  const onHoverStart = () => {
    setHover(true);
  };
  const onHoverEnd = () => {
    setHover(false);
  };

  const onTileClick = () => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'tile',
      actionSubjectId: 'templateTile',
      source: analyticsSource ? analyticsSource : 'templateGalleryScreen',
      attributes: {
        category,
        shortLink,
      },
    });
  };

  const routerLinkProps = {
    className: styles.allText,
    href: `/templates/${makeTemplateUrlSlug(category, title, shortLink)}`,
    title,
    onClick: onTileClick,
  };

  const { loading, templateBackgroundStyle } = useTemplateBackgroundStyle(
    boardId,
    backgroundColor,
    backgroundUrl,
  );
  const templateBackgroundAfterStyle = getBackgroundAfterStyle(hover);

  return (
    <div
      data-testid={getTestId<TemplatesTestIds>('templates-tile-container')}
      className={classNames(styles.tileContainer, {
        [styles.isFeatured]: isFeatured,
      })}
    >
      <RouterLink {...routerLinkProps}>
        {loading ? (
          <TemplatesTileSkeleton />
        ) : (
          <div
            className={styles.templateBackground}
            style={templateBackgroundStyle}
          />
        )}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className={styles.templateBackgroundAfter}
          style={templateBackgroundAfterStyle}
          onMouseEnter={onHoverStart}
          onMouseLeave={onHoverEnd}
        />
        <div className={styles.templateInfo}>
          <MemberAvatarUnconnected
            className={styles.memberAvatar}
            fullName={convertToPIIString(byline)}
            avatars={{
              '30': `${avatarUrl}/30.png`,
              '50': `${avatarUrl}/50.png`,
              '170': `${avatarUrl}/170.png`,
            }}
            size={48}
            avatarClassName={classNames(styles.avatar, {
              [styles.orgAvatar]: avatarShape === 'square',
            })}
          />
          <LinesEllipsis text={title} maxLine="2" className={styles.title} />
          <div className={styles.author}>
            {intl
              .formatMessage(
                {
                  id: 'templates.templates_tile.by',
                  defaultMessage: 'by {author}',
                  description: 'The author of the board',
                },
                {
                  author: byline,
                },
              )
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&')}
          </div>
          {!!blurb && (
            <LinesEllipsis text={blurb} maxLine="3" className={styles.blurb} />
          )}
          <div className={styles.viewsAndCopies}>
            <ViewsAndCopies
              isTemplate={true}
              showLabels={false}
              useSmallTextStyle={true}
              copyCount={copyCount}
              viewCount={viewCount}
            />
          </div>
        </div>
      </RouterLink>
    </div>
  );
};
